// Ad-copy bridge pages for every degree level + program combination, served
// at /explore/{degree}-{program} (e.g. /explore/bachelors-accounting).
//
// There are ~480 of these, so they are GENERATED from the same option lists
// the forms use (data/filterOptions.ts) rather than hand-authored. A light
// index of every combination is built once for the directory and search; the
// full page content is composed on demand when a slug is requested.
//
// Clicking the CTA sends this page's degreeInterest and mProgram values to
// the Media-Matchers Clicks Search, so the visitor arrives already matched to
// the degree level and program the ad promised.
import {
  degreeLevelOptions,
  areaStudyOptions,
  programOptions,
} from './filterOptions'
import { areaOfStudyData } from './areaOfStudyData'
// Type-only import keeps this module free of a runtime circular dependency
// (exploreBridges imports getProgramBridge from here).
import type { ExploreBridge } from './exploreBridges'

// Publisher group used when the incoming URL carries no publisher params.
const DEFAULT_PUB_GROUP_ID = 'PCGP3406855604'

// Hero imagery per area of study. One photograph of a real person working in
// the field, shared by every program in that area.
const AREA_HERO: Record<number, { image: string; alt: string }> = {
  1: {
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/43f97c46-3cd7-45ce-9d16-ca563ae6164e.jpg',
    alt: 'Woman working on a laptop at a desk in a bright open-plan office',
  },
  2: {
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/29e3ffc5-88ce-40b1-8e97-3b9c26aabf6e.jpg',
    alt: 'Woman in a uniform shirt standing in a sunlit courthouse corridor',
  },
  3: {
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/28b1f89a-63ab-4542-b017-a5628820916a.jpg',
    alt: 'Counselor listening attentively in a softly lit therapy office',
  },
  4: {
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/9dad44cf-6b65-4d54-abba-0d17b90191b8.jpg',
    alt: 'Man working at a dual-monitor computer setup in a home workspace',
  },
  5: {
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/0f68e8e3-a0fd-49b3-a584-52e4b0f1d37b.jpg',
    alt: 'Medical assistant in scrubs smiling in a bright clinic hallway',
  },
  6: {
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/0e8bb1f8-57b4-4371-b777-455a548b0d73.jpg',
    alt: 'Teacher helping young students at a table in a sunlit classroom',
  },
  7: {
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/3723d3a3-2d2a-44e0-8acb-a7e22eff907d.jpg',
    alt: 'Tradesperson in safety glasses inspecting equipment in a workshop',
  },
  8: {
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/4fa40362-12cb-4359-a947-d00d5c1fefd9.jpg',
    alt: 'Student reading and taking notes at a table in a sunlit library',
  },
  9: {
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/91e72bea-5287-4f80-a0c8-ca9ba33b4b77.jpg',
    alt: 'Three adult students talking together on a sunlit campus walkway',
  },
  10: {
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/1bc48891-02cb-432c-bb72-0dc5618f544c.jpg',
    alt: 'Nurse in scrubs speaking with a patient in a bright hospital room',
  },
  11: {
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/9e5473c7-62b9-4930-848d-2fb1c11a6b7d.jpg',
    alt: 'Videographer editing footage at a desk with a camera and microphone',
  },
  12: {
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/2ac424b6-67ad-430b-98a9-832ff6ba454f.jpg',
    alt: 'Designer sketching layouts on a tablet at a bright studio desk',
  },
  13: {
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/8a78ff13-e585-4522-a7b8-cb4262b56cff.jpg',
    alt: 'Man reading at a wooden table beside a tall window in a quiet study',
  },
}

// How each degree level is framed in the ad copy: what it is, how long it
// takes, and who it suits.
const DEGREE_COPY: Record<
  number,
  { slug: string; short: string; long: string; pitch: string; timing: string }
> = {
  1: {
    slug: 'certificate',
    short: 'Certificate',
    long: 'certificate',
    pitch:
      'A certificate is the fastest way in. Coursework stays tightly focused on the skills employers screen for, so you spend your time on what the job actually asks of you.',
    timing: 'Many students finish in under a year',
  },
  2: {
    slug: 'associates',
    short: "Associate's",
    long: "associate degree",
    pitch:
      'An associate degree gives you a credential that stands on its own in the job market, and credits that can transfer toward a bachelor’s later if you decide to keep going.',
    timing: 'Typically about two years part or full time',
  },
  3: {
    slug: 'bachelors',
    short: "Bachelor's",
    long: "bachelor's degree",
    pitch:
      'A bachelor’s degree is the credential most employers list on postings in this field, and the one that tends to open the widest range of roles and pay bands.',
    timing: 'Typically four years, fewer with transfer credit',
  },
  4: {
    slug: 'masters',
    short: "Master's",
    long: "master's degree",
    pitch:
      'A master’s degree is built for people already working in the field who want to move into senior, specialized, or leadership roles.',
    timing: 'Typically one to two years',
  },
}

export interface ProgramBridgeEntry {
  slug: string
  degreeInterest: number
  degreeLabel: string
  mProgram: number
  programLabel: string
  areaId: number
  areaLabel: string
}

// The catalog's "×00" entries are the catch-all option for their area and are
// labeled for a dropdown ("General Business", "All Nursing Programs
// (General)"). As a page headline that reads as a hedge, so the word is
// stripped and the area itself becomes the subject.
const GENERAL_LABEL_OVERRIDES: Record<number, string> = {
  // "General/Undecided" has nothing left once the word is removed.
  900: 'Online Degrees',
}
function displayProgramLabel(value: number, label: string): string {
  if (value % 100 !== 0) return label
  if (GENERAL_LABEL_OVERRIDES[value]) return GENERAL_LABEL_OVERRIDES[value]
  return label
    .replace(/^general\s+/i, '')
    .replace(/^all\s+/i, '')
    .replace(/\s*\(general\)\s*$/i, '')
    .replace(/\s+programs$/i, '')
    .trim()
}

const slugify = (value: string): string =>
  value
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')

// Built once at module load: one entry per degree level × program pairing.
// Program slugs are made unique across areas so two areas offering the same
// program name (e.g. Cyber Security) don't collide on one URL.
function buildIndex(): ProgramBridgeEntry[] {
  const programSlugCounts = new Map<string, number>()
  const programSlugs = new Map<number, string>()
  areaStudyOptions.forEach((area) => {
    ;(programOptions[area.value] || []).forEach((program) => {
      const base = slugify(displayProgramLabel(program.value, program.label))
      const seen = programSlugCounts.get(base) || 0
      programSlugCounts.set(base, seen + 1)
      programSlugs.set(
        program.value,
        seen === 0 ? base : `${base}-${slugify(area.label)}`,
      )
    })
  })
  const entries: ProgramBridgeEntry[] = []
  degreeLevelOptions.forEach((degree) => {
    areaStudyOptions.forEach((area) => {
      ;(programOptions[area.value] || []).forEach((program) => {
        entries.push({
          slug: `${DEGREE_COPY[degree.value].slug}-${programSlugs.get(program.value)}`,
          degreeInterest: degree.value,
          degreeLabel: degree.label,
          mProgram: program.value,
          programLabel: displayProgramLabel(program.value, program.label),
          areaId: area.value,
          areaLabel: area.label,
        })
      })
    })
  })
  return entries
}

export const programBridgeIndex: ProgramBridgeEntry[] = buildIndex()

const bySlug = new Map(programBridgeIndex.map((entry) => [entry.slug, entry]))

// Numeric shorthand: /explore/{degreeInterest}-{mProgram}, e.g. /explore/3-101
// resolves to /explore/bachelors-accounting. Lets a traffic source build the
// URL straight from the codes it already sends.
const byCodes = new Map(
  programBridgeIndex.map((entry) => [
    `${entry.degreeInterest}-${entry.mProgram}`,
    entry,
  ]),
)

// Returns the named slug for a numeric shorthand path, or undefined when the
// slug isn't numeric shorthand or the combination doesn't exist.
export function resolveProgramCodeSlug(
  slug: string | undefined,
): string | undefined {
  if (!slug || !/^\d+-\d+$/.test(slug)) return undefined
  return byCodes.get(slug)?.slug
}

// Composes the full page content for one degree + program combination.
function composeBridge(entry: ProgramBridgeEntry): ExploreBridge {
  const degree = DEGREE_COPY[entry.degreeInterest]
  const area = areaOfStudyData[entry.areaId]
  const hero = AREA_HERO[entry.areaId] || AREA_HERO[9]
  const program = entry.programLabel
  const paragraphs = [
    `${degree.pitch} ${degree.timing}, and online programs let you keep working while you study.`,
  ]
  if (area?.careerOutlook) paragraphs.push(area.careerOutlook)
  if (area?.whatToLookFor) paragraphs.push(area.whatToLookFor)
  const jobTitles = area?.jobTitles?.slice(0, 3).join(', ')
  // The same program at the other degree levels — the most common next
  // question after "is this the right subject?" is "which level do I need?"
  const otherLevels = programBridgeIndex
    .filter(
      (other) =>
        other.mProgram === entry.mProgram &&
        other.degreeInterest !== entry.degreeInterest,
    )
    .map((other) => ({
      label: `${other.degreeLabel} in ${other.programLabel}`,
      to: `/explore/${other.slug}`,
    }))
  // Sibling programs in the same area at this degree level, for visitors who
  // know the field but haven't settled on a specialization.
  const siblings = programBridgeIndex
    .filter(
      (other) =>
        other.areaId === entry.areaId &&
        other.degreeInterest === entry.degreeInterest &&
        other.mProgram !== entry.mProgram,
    )
    .slice(0, 8)
    .map((other) => ({
      label: other.programLabel,
      to: `/explore/${other.slug}`,
    }))
  const relatedGroups = [
    otherLevels.length && {
      heading: `Other degree levels in ${program}`,
      items: otherLevels,
    },
    siblings.length && {
      heading: `More ${entry.areaLabel} programs`,
      items: siblings,
    },
  ].filter(Boolean) as { heading: string; items: { label: string; to: string }[] }[]
  return {
    slug: entry.slug,
    brand: 'FindOnlineDegrees',
    clicksSearch: {
      defaultPubGroupID: DEFAULT_PUB_GROUP_ID,
      defaultDegreeInterest: String(entry.degreeInterest),
      defaultAreaStudy: String(entry.areaId),
      defaultMProgram: String(entry.mProgram),
    },
    metaTitle: `Online ${degree.short} in ${program} | FindOnlineDegrees`,
    metaDescription: `Compare accredited online ${degree.long} programs in ${program} and get matched with schools enrolling now.`,
    heroImage: hero.image,
    heroImageAlt: hero.alt,
    heroHeadline: `Earn Your ${degree.short} in ${program} Online`,
    cardHeadline: `Get Matched With ${program} Programs`,
    cardSubhead: 'Financial aid may be available for those who qualify',
    ctaLabel: 'Get Program Information',
    sectionHeading: `Why an Online ${degree.short} in ${program}?`,
    paragraphs,
    relatedGroups,
    closingLine: jobTitles
      ? `See which schools are enrolling ${program} students — roles graduates pursue include ${jobTitles}.`
      : `See which schools are currently enrolling ${program} students.`,
    disclosure:
      'FindOnlineDegrees is an advertising-supported site. We may receive compensation when you request information from the schools featured here. Program availability varies by school; not every school offers every program at every degree level.',
  }
}

export function getProgramBridge(
  slug: string | undefined,
): ExploreBridge | undefined {
  if (!slug) return undefined
  const entry = bySlug.get(slug.toLowerCase())
  return entry ? composeBridge(entry) : undefined
}
